/**
 * Pure helpers for the Country Admin "Vehicle Variant" modal.
 *
 * Kept free of React so the payload shaping can be unit-tested in isolation
 * (mirrors `pages/brokers/brokerRateForm.ts`).
 *
 * The modal used to POST its raw form state. That is unsafe against
 * `VehicleVariantCreateUpdateSerializer` for two reasons:
 *
 *  - `fuel_type` is a NOT NULL FK (`on_delete=PROTECT`). DRF's RelatedField
 *    coerces `''` to `null` before validating, so a blank select produced the
 *    field-less "This field may not be null." that started F3.
 *  - `engine_cc`, `cc`, `gvw` and `ex_showroom_price` are nullable numerics.
 *    An empty string is not a valid integer/decimal, so a blank optional
 *    input failed the whole request.
 */

export interface VehicleVariantFormState {
  model: string;
  name: string;
  code: string;
  fuel_type: string;
  transmission: string;
  engine_cc: string;
  cc: string;
  gvw: string;
  ex_showroom_price: string;
}

export type VehicleVariantPayload = Record<string, string | number | null>;

/**
 * Every field the serializer actually requires, with the label the form shows.
 * `transmission` is absent on purpose — the model defaults it to "manual".
 */
const REQUIRED_LABELS: Record<string, string> = {
  model: 'Model',
  name: 'Name',
  code: 'Code',
  fuel_type: 'Fuel Type',
};

/** Nullable numeric columns on `VehicleVariant`, in form order. */
const NUMERIC_FIELDS = ['engine_cc', 'ex_showroom_price', 'cc', 'gvw'] as const;

/** Text/FK fields posted verbatim when set, omitted when blank. */
const TEXT_FIELDS = ['model', 'name', 'code', 'fuel_type', 'transmission'] as const;

/**
 * Validate client-side before the request goes out. Returns a map of
 * ``field -> message`` for every missing required field (empty when valid), so
 * the user is told which control to fill rather than reading a server message
 * that names no field.
 */
export function validateVariantForm(form: VehicleVariantFormState): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const [field, label] of Object.entries(REQUIRED_LABELS)) {
    const value = form[field as keyof VehicleVariantFormState];
    if (!value || !value.trim()) {
      errors[field] = `${label} is required.`;
    }
  }
  return errors;
}

/**
 * Shape the form state into a request body the serializer accepts.
 *
 * Blank text/FK values are DROPPED rather than sent as `''` — see the module
 * docstring for why an empty `fuel_type` is the one that breaks hardest.
 *
 * Blank numerics differ by mode: on create there is nothing to clear so they
 * are omitted (the column defaults to NULL), while on update an explicit
 * `null` is sent so an edit can CLEAR a previously-set value (CA-1). A value
 * that will not parse as a number is treated as blank; posting `NaN` would
 * serialise to `null` and silently wipe the field.
 */
export function buildVariantPayload(
  form: VehicleVariantFormState,
  options: { mode: 'create' | 'update' },
): VehicleVariantPayload {
  const payload: VehicleVariantPayload = {};

  for (const field of TEXT_FIELDS) {
    const value = form[field]?.trim();
    if (value) {
      payload[field] = value;
    }
  }

  for (const field of NUMERIC_FIELDS) {
    const raw = form[field]?.trim();
    const parsed = raw ? Number(raw) : NaN;
    if (raw && Number.isFinite(parsed)) {
      payload[field] = parsed;
    } else if (options.mode === 'update') {
      payload[field] = null;
    }
  }

  return payload;
}
